import { Box, Typography, Tabs, Tab } from '@mui/material'
import PropTypes from 'prop-types'
import { useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useState, useEffect } from 'react'
// import HeaderWithPopover from 'src/components/HeaderWithPopover'
import { useAuth } from 'src/auth/auth'
import { useGetMeAndWorkspaceQuery } from 'src/services/user-service'
// import BilingPage from 'src/components/settings/BillingPage'
import UpgradePage from 'src/components/settings/BillingPage'
import UsagePage from 'src/components/settings/UsagePage'
import MyAccountPage from 'src/components/settings/MyAccountPage'
import WorkspacePage from 'src/components/settings/WorkspacePage'
import BlocklistPage from 'src/components/settings/BlocklistPage'
import { useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import BillingUpdatedPage from 'src/components/settings/BillingUpdatedPage'
// import IntegrationsAndWebhookPage from './integrationsAndWebhook'
import useUserVerification from 'src/hooks/useUserVerification'
import HeaderWithPopoverUpdated from 'src/components/HeaderWithPopoverUpdated'

function CustomTabPanel (props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{}}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
}

const SettingsUpdated = ({ tab }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const fromCreateWorkspace = location.state?.fromCreateWorkspace || false
  const { isExcludedIntegrationFeature } = useUserVerification()

  const { canEdit, canDelete } = useAuth()

  if (fromCreateWorkspace) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column'
        }}
      >
        <UpgradePage
          fromCreateWorkspace={fromCreateWorkspace}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </Box>
    )
  }

  // Original implementation for the regular settings page
  const theme = useTheme()
  const user = useSelector((state) => state.user)
  const isPartner = user?.isPartner
  const partnerAccountId = user?.partnerAccountId
  const { data, refetch } = useGetMeAndWorkspaceQuery()

  // Map tab names to values
  const getTabValue = (tabName) => {
    if (isPartner || partnerAccountId) {
      // Whitelabel users
      if (isExcludedIntegrationFeature) {
        // Integrations tab is hidden
        switch (tabName) {
          case 'myAccount': return 0
          case 'myPlan': return 1
          case 'blocklist': return 2
          default: return 0
        }
      } else {
        // Integrations tab is shown
        switch (tabName) {
          case 'myAccount': return 0
          case 'myPlan': return 1
          case 'integrations': return 2
          case 'blocklist': return 3
          default: return 0
        }
      }
    } else {
      // Non-whitelabel users
      switch (tabName) {
        case 'myAccount': return 0
        case 'workspace': return 1
        case 'myPlan': return 2
        case 'billing': return 3
        case 'upgrade': return 4
        case 'integrations': return 5
        case 'blocklist': return 6
        default: return 0
      }
    }
  }

  const [value, setValue] = useState(getTabValue(tab))
  const [selectedTimeline, setSelectedTimeline] = useState(data?.workspace)

  // Map values back to tab names for navigation
  const getTabRoute = (tabValue) => {
    if (isPartner || partnerAccountId) {
      // Whitelabel users
      if (isExcludedIntegrationFeature) {
        // Integrations tab is hidden
        switch (tabValue) {
          case 0: return 'myAccount'
          case 1: return 'myPlan'
          case 2: return 'blocklist'
          default: return 'profile'
        }
      } else {
        // Integrations tab is shown
        switch (tabValue) {
          case 0: return 'myAccount'
          case 1: return 'myPlan'
          case 2: return 'integrations'
          case 3: return 'blocklist'
          default: return 'profile'
        }
      }
    } else {
      // Non-whitelabel users
      switch (tabValue) {
        case 0: return 'myAccount'
        case 1: return 'workspace'
        case 2: return 'myPlan'
        case 3: return 'billing'
        case 4: return 'upgrade'
        case 5: return 'integrations'
        case 6: return 'blocklist'
        default: return 'profile'
      }
    }
  }

  const handleChange = (event, newValue) => {
    setValue(newValue)
    if (newValue === 0) {
      refetch()
    }

    // Navigate to the corresponding route
    const route = getTabRoute(newValue)
    navigate(`/settingsUpdated/${route}`)
  }

  const handleWorkspaceChange = (timeline) => {
    setSelectedTimeline(timeline)
  }

  useEffect(() => {
    setValue(getTabValue(tab))
  }, [tab, isPartner, partnerAccountId, isExcludedIntegrationFeature])

  // logic to determine if integration tab should be shown
  const shouldShowIntegrations = () => {
    // If user is whitelabel (partner or has partnerAccountId)
    if (isPartner || partnerAccountId) {
      // For whitelabel users, show only if NOT excluded
      return !isExcludedIntegrationFeature
    }
    // For normal SuccessAI users, always show
    return true
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
          display: 'none' // Hide scrollbar for WebKit browsers
        }
      }}
    >
      <Box
        sx={{
          width: '96%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}
      >

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            pb: 2
          }}
        >
          <HeaderWithPopoverUpdated
            title='Settings'
            onWorkspaceChange={handleWorkspaceChange}
            selectedTimeline={selectedTimeline}
          />
        </Box>
        <Box
          sx={{
            boxShadow: '0px 12px 15px 0px #4B71970D',
            borderRadius: '12px',
            width: '100%',
            p: { xs: 1, sm: 2, md: 3 },
            // mt: 3,
            overflowX: 'auto'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              background: '#E1E3EC',
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              width: { xs: '100%', sm: '100%', md: '1160px' },
              minWidth: { xs: 'min-content', md: 'auto' }
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              textColor='black'
              variant={{ xs: 'scrollable', md: 'standard' }}
              scrollButtons='auto'
              allowScrollButtonsMobile={false}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                background: '#E1E3EC',
                borderRadius: '8px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#6c757d',
                  borderRadius: '8px',
                  px: { xs: 2, sm: 6 }, // Smaller padding on mobile
                  fontSize: { xs: '0.875rem', sm: '14px' } // Smaller font on mobile
                },
                '& .Mui-selected': {
                  backgroundColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.main,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='My Account' />
              {!(isPartner || partnerAccountId) && (
                <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='Workspace' />
              )}
              <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='My Plan' />
              {!(isPartner || partnerAccountId) && (
                <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='Billing' />
              )}
              {!(isPartner || partnerAccountId) && (
                <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='Upgrade' />
              )}
              {shouldShowIntegrations() && (
                <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='Integrations' />
              )}
              <Tab sx={{ my: '5px', mx: '2px', px: 6, py: '4px' }} label='Blocklist' />
            </Tabs>
          </Box>
        </Box>
        <Box
          sx={{
            mb: 4,
            mt: 3,
            width: '100%'
          }}
        >
          <CustomTabPanel value={value} index={0}>
            <MyAccountPage canEdit={canEdit} />
          </CustomTabPanel>
          {!(isPartner || partnerAccountId) && (
            <CustomTabPanel value={value} index={1}>
              <WorkspacePage setSelectedTimeline={setSelectedTimeline} canEdit={canEdit} />
            </CustomTabPanel>
          )}
          <CustomTabPanel value={value} index={isPartner || partnerAccountId ? 1 : 2}>
            <UsagePage setValue={setValue} canEdit={canEdit} />
          </CustomTabPanel>
          {!(isPartner || partnerAccountId) && (
            <CustomTabPanel value={value} index={3}>
              <BillingUpdatedPage setValue={setValue} canEdit={canEdit} />
            </CustomTabPanel>
          )}
          {!(isPartner || partnerAccountId) && (
            <CustomTabPanel value={value} index={4}>
              {/* <BilingPage
                fromCreateWorkspace={fromCreateWorkspace}
                canEdit={canEdit}
                canDelete={canDelete}
                setTab={setValue}
              /> */}
              <UpgradePage
                fromCreateWorkspace={fromCreateWorkspace}
                canEdit={canEdit}
                canDelete={canDelete}
                setTab={setValue}
              />
            </CustomTabPanel>
          )}
          {shouldShowIntegrations() && (
            <CustomTabPanel value={value} index={isPartner || partnerAccountId ? 2 : 5}>
              {/* <IntegrationsAndWebhookPage canEdit={canEdit} /> */}
              <Outlet />
            </CustomTabPanel>
          )}
          <CustomTabPanel value={value} index={isPartner || partnerAccountId ? (isExcludedIntegrationFeature ? 2 : 3) : 6}>
            <BlocklistPage campaign={[]} canEdit={canEdit} />
          </CustomTabPanel>
        </Box>
      </Box>
    </Box>
  )
}

SettingsUpdated.propTypes = {
  tab: PropTypes.string.isRequired
}

export default SettingsUpdated